import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config/api";

function Intro() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [intro, setIntro] = useState({
    welcomeText: "Hi, I'm",
    firstName: "Mohammed Junaid",
    lastName: "Hussain",
    roles: ["Full Stack Developer", "ML Enthusiast"],
    caption: "Full Stack Developer • ML Enthusiast",
    description:
      "I build modern web applications and intelligent systems with an emphasis on clean design, performance, and real-world problem solving. I love exploring new technologies, solving challenges, and turning ideas into meaningful solutions.",
    resumeUrl: "",
  });
  const [loading, setLoading] = useState(true);
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    const fetchIntro = async (retries = 3, delay = 1000) => {
      try {
        const response = await axios.get(`${API_URL}/intro`);
        if (response.data && response.data.success) {
          const introData = response.data.data;
          setIntro(Array.isArray(introData) ? introData[0] : introData);
        } else if (response.data && response.data.length > 0) {
          setIntro(response.data[0]);
        }
        setLoading(false);
      } catch (error) {
        if (error.response?.status === 429 && retries > 0) {
          console.log(
            `Rate limited. Retrying in ${delay}ms... (${retries} attempts left)`
          );
          setTimeout(() => fetchIntro(retries - 1, delay * 2), delay);
          return;
        }
        console.error("Error fetching intro:", error);
        setLoading(false);
      }
    };

    fetchIntro();
  }, []);

  // Typing animation effect
  useEffect(() => {
    if (!intro.roles || intro.roles.length === 0) return;

    const currentRole = intro.roles[currentRoleIndex];
    const typingSpeed = isDeleting ? 50 : 100;
    const pauseTime = isDeleting ? 500 : 2000;

    const timer = setTimeout(() => {
      if (!isDeleting && currentText === currentRole) {
        // Pause before deleting
        setTimeout(() => setIsDeleting(true), pauseTime);
      } else if (isDeleting && currentText === "") {
        // Move to next role
        setIsDeleting(false);
        setCurrentRoleIndex((prev) => (prev + 1) % intro.roles.length);
      } else if (!isDeleting) {
        // Typing
        setCurrentText(currentRole.substring(0, currentText.length + 1));
      } else {
        // Deleting
        setCurrentText(currentRole.substring(0, currentText.length - 1));
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [currentText, isDeleting, currentRoleIndex, intro.roles]);

  if (loading) {
    return (
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="text-white/30 text-sm tracking-wider uppercase">
          Loading...
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Subtle animated background */}
      <div className="absolute inset-0 opacity-[0.015]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255,255,255,0.03) 0%, transparent 50%)`,
            transition: "background-image 0.3s ease",
          }}
        ></div>
      </div>

      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
            backgroundSize: "80px 80px",
          }}
        ></div>
      </div>

      {/* Content Container */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        {/* Welcome indicator */}
        <div className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8 opacity-0 animate-fadeInUp">
          <div className="w-6 sm:w-8 h-[1px] bg-gradient-to-r from-transparent to-white/40"></div>
          <span className="text-white/70 text-sm sm:text-base md:text-lg tracking-[0.2em] sm:tracking-[0.3em] uppercase font-light">
            {intro.welcomeText}
          </span>
        </div>

        {/* Name - Large Impact */}
        <div
          className="mb-4 sm:mb-6 opacity-0 animate-fadeInUp"
          style={{ animationDelay: "0.1s" }}
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extralight tracking-tight leading-tight">
            <span className="text-white/90 block">{intro.firstName}</span>
            <span className="text-white/30 block mt-1 sm:mt-2">
              {intro.lastName}
            </span>
          </h1>
        </div>

        {/* Role with typing animation */}
        <div
          className="mb-8 sm:mb-12 opacity-0 animate-fadeInUp"
          style={{ animationDelay: "0.2s" }}
        >
          <div className="inline-flex items-center gap-3 sm:gap-4">
            <div className="w-1 h-1 rounded-full bg-white/40 animate-pulse"></div>
            <p className="text-white/40 text-base sm:text-lg md:text-xl font-light tracking-wide">
              {currentText}
              <span className="animate-pulse">|</span>
            </p>
          </div>
        </div>

        {/* Description */}
        <div
          className="max-w-2xl mb-12 sm:mb-16 opacity-0 animate-fadeInUp"
          style={{ animationDelay: "0.3s" }}
        >
          <p className="text-white/40 text-base sm:text-base md:text-lg leading-relaxed font-light">
            {intro.description}
          </p>
        </div>

        {/* Minimal CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 opacity-0 animate-fadeInUp"
          style={{ animationDelay: "0.4s" }}
        >
          {/* Resume Button - Primary */}
          <a
            href="/Final_Resume_wp.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative px-6 sm:px-8 py-3 sm:py-4 overflow-hidden w-full sm:w-auto text-center"
          >
            <div
              className="absolute inset-0 bg-white/[0.02] border border-white/10 rounded-full
                          group-hover:bg-white/[0.04] group-hover:border-white/20 
                          transition-all duration-500"
            ></div>
            <span
              className="relative text-white/70 group-hover:text-white/90 text-xs sm:text-sm tracking-wider 
                           uppercase font-light transition-colors duration-500 flex items-center justify-center gap-2"
            >
              View Resume
              <svg
                className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform duration-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </span>
          </a>

          {/* Contact Button - Secondary */}
          <a
            href="#contact"
            className="group relative px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto text-center"
          >
            <div
              className="absolute inset-0 border border-white/[0.06] rounded-full
                          group-hover:border-white/10 transition-all duration-500"
            ></div>
            <span
              className="relative text-white/40 group-hover:text-white/60 text-xs sm:text-sm tracking-wider 
                           uppercase font-light transition-colors duration-500"
            >
              Contact Me
            </span>
          </a>
        </div>

        {/* Scroll Indicator */}
        <div
          className="absolute bottom-12 left-1/2 -translate-x-1/2 opacity-0 animate-fadeInUp"
          style={{ animationDelay: "0.6s" }}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="w-[1px] h-16 bg-gradient-to-b from-white/20 to-transparent"></div>
            <div className="w-1 h-1 rounded-full bg-white/20 animate-bounce"></div>
          </div>
        </div>
      </div>

      {/* Floating accent element */}
      <div
        className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-white/[0.008] 
                    blur-[150px] rounded-full animate-float"
      ></div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0);
          }
          50% {
            transform: translate(30px, -30px);
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 1s ease-out forwards;
        }
        
        .animate-float {
          animation: float 20s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}

export default Intro;
